import { useEffect, useMemo, useState } from 'react';
import {
  Search, Users, Coins, TriangleAlert, ArrowLeft, Plus, Minus,
  Sparkles, Clock,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, StatCard } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { Badge } from '@/components/ui/Badge';
import { LoadingState } from '@/components/ui/States';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { ApiError } from '@/lib/api/client';
import { cn } from '@/lib/cn';
import { getLoyaltyMembers, getLoyaltyLedger, adjustLoyaltyPoints } from '../api';
import type { LoyaltyMember, LoyaltyMembersResponse, LoyaltyLedgerRow, LoyaltyEntryType } from '../types';

const rupees = (v: number | string | null | undefined) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 })
    .format(parseFloat(String(v ?? 0)) || 0);

const shortDate = (v: string | null) =>
  v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';

const shortDateTime = (v: string | null) =>
  v ? new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

/**
 * How each ledger entry type reads to a human. The wording matters more than it
 * looks: "Taken back" and "Expired" are the two rows a customer will query at
 * the counter, and staff need to be able to explain them without interpreting a
 * database enum.
 */
const ENTRY_LABEL: Record<LoyaltyEntryType, string> = {
  earn: 'Earned',
  redeem: 'Used',
  expire: 'Expired',
  adjust: 'Manual adjustment',
  return_reversal: 'Taken back (return)',
  return_refund: 'Given back (return)',
};

/**
 * Members — who holds pharmacy loyalty points, and the ledger behind each one.
 *
 * Two jobs this screen exists for:
 *
 *   1. **The liability.** Outstanding points are a promise to discount future
 *      sales, so somebody in finance eventually asks what they are worth. That
 *      figure covers the WHOLE programme and never moves when a search is typed.
 *
 *   2. **Answering a customer.** "Where did my points go?" is settled by the
 *      ledger, not by a balance — every earn, use, expiry and correction is one
 *      row saying what it came from.
 *
 * It is also the only place `POST /api/loyalty/adjust` is reachable from: a
 * goodwill grant or the correction of an operator error, always attributed and
 * always reasoned.
 */
export default function MembersPanel() {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [data, setData] = useState<LoyaltyMembersResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState<LoyaltyMember | null>(null);

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(query.trim()), 400);
    return () => clearTimeout(handle);
  }, [query]);

  const load = useMemo(
    () => (q: string) => {
      setLoading(true);
      getLoyaltyMembers(q || undefined)
        .then((d) => { setData(d); setError(''); })
        .catch((e: unknown) => setError(e instanceof ApiError ? e.message : String(e)))
        .finally(() => setLoading(false));
    },
    []
  );

  useEffect(() => { load(debounced); }, [debounced, load]);

  if (open) {
    return <MemberLedger member={open} pointValue={data?.point_value ?? 1} onBack={() => { setOpen(null); load(debounced); }} />;
  }

  const members = data?.members ?? [];
  const summary = data?.summary;

  return (
    <div className="space-y-4" style={{ maxWidth: 1000 }}>
      {error && <div className="rounded-lg bg-danger-soft px-3 py-2 text-xs text-danger-ink">{error}</div>}

      {data && !data.enabled && (
        <div className="flex gap-2.5 rounded-lg bg-warn-soft px-3 py-2.5 text-[11.5px] text-caution-ink">
          <TriangleAlert size={15} className="mt-px shrink-0" />
          <p className="leading-relaxed">
            The programme is currently <b>off</b>, so no new points are being earned. Balances
            already held are untouched and still listed here — turn it back on from the Loyalty Points tab
            and they become spendable again.
          </p>
        </div>
      )}

      {summary && (
        <div className="grid3">
          <StatCard accentSide="left" accentColor="var(--teal)">
            <div className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">Members</div>
            <div className="mt-1 text-[22px] font-bold leading-none">{summary.members}</div>
            <div className="mt-1 text-[11px] text-ink-muted">customers holding an account</div>
          </StatCard>
          <StatCard accentSide="left" accentColor="var(--color-warn)">
            <div className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">Points outstanding</div>
            <div className="mt-1 text-[22px] font-bold leading-none">{summary.points_outstanding.toLocaleString('en-IN')}</div>
            <div className="mt-1 text-[11px] text-ink-muted">unspent and not yet expired</div>
          </StatCard>
          <StatCard accentSide="left" accentColor="var(--color-danger)">
            <div className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">Worth</div>
            <div className="mt-1 text-[22px] font-bold leading-none">{rupees(summary.value_outstanding)}</div>
            <div className="mt-1 text-[11px] text-ink-muted">if every point were spent</div>
          </StatCard>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle><Users size={15} /> Members</CardTitle>
          {members.length > 0 && <Badge variant="neutral">{members.length} shown</Badge>}
        </CardHeader>

        <div className="relative mb-3.5">
          <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <Input
            value={query}
            placeholder="Search by name, mobile or UHID"
            className="pl-9"
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        {loading && !data ? (
          <LoadingState />
        ) : members.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border px-4 py-8 text-center">
            <div className="text-[13px] text-ink-muted">
              {debounced ? `Nobody matches “${debounced}”` : 'Nobody holds points yet'}
            </div>
            {!debounced && (
              <div className="mt-1 text-[11.5px] text-ink-muted/75">
                Accounts appear here after a customer's first pharmacy purchase.
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead className="text-right">Available</TableHead>
                  <TableHead className="text-right">Worth</TableHead>
                  <TableHead>Expiring</TableHead>
                  <TableHead>Last activity</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((m) => (
                  <TableRow key={m.patient_id}>
                    <TableCell>
                      <div className="text-[12.5px] font-medium">{m.name}</div>
                      <div className="text-[10.5px] text-ink-muted">
                        <span className="font-mono">{m.uhid}</span> · {m.mobile}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">{m.points_available}</TableCell>
                    <TableCell className="text-right tabular-nums text-ink-muted">
                      {rupees(m.points_available * (data?.point_value ?? 1))}
                    </TableCell>
                    <TableCell>
                      {m.points_expiring_soon > 0 ? (
                        <Badge variant="warn" className="inline-flex items-center gap-1">
                          <Clock size={10} /> {m.points_expiring_soon} by {shortDate(m.next_expiry)}
                        </Badge>
                      ) : (
                        <span className="text-[11px] text-ink-muted">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-[11.5px] text-ink-muted">{shortDate(m.last_activity_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => setOpen(m)}>Statement</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ── One member's ledger ──────────────────────────────────────────

function MemberLedger({
  member, pointValue, onBack,
}: {
  member: LoyaltyMember;
  pointValue: number;
  onBack: () => void;
}) {
  const [rows, setRows] = useState<LoyaltyLedgerRow[] | null>(null);
  const [error, setError] = useState('');
  const [adjusting, setAdjusting] = useState(false);
  const [direction, setDirection] = useState<'add' | 'deduct'>('add');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    getLoyaltyLedger(member.patient_id)
      .then((r) => { setRows(r); setError(''); })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : String(e)));
  };
  useEffect(load, [member.patient_id]);

  const submit = async () => {
    const n = parseInt(amount, 10);
    if (!Number.isInteger(n) || n <= 0) { setError('Enter a whole number of points greater than zero.'); return; }
    if (!note.trim()) { setError('A reason is required — every adjustment is attributed and auditable.'); return; }
    setSaving(true); setError('');
    try {
      await adjustLoyaltyPoints({
        patient_id: member.patient_id,
        points: direction === 'add' ? n : -n,
        note: note.trim(),
      });
      setAdjusting(false); setAmount(''); setNote('');
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
    setSaving(false);
  };

  // Recomputed from the ledger rather than reusing the list's figure, which is
  // now one adjustment out of date.
  const balance = rows ? rows.reduce((sum, r) => sum + r.points, 0) : member.points_available;

  return (
    <div className="space-y-4" style={{ maxWidth: 1000 }}>
      <Card>
        <div className="flex flex-wrap items-start gap-3">
          <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft size={13} /> All members</Button>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold">{member.name}</div>
            <div className="mt-0.5 text-[11.5px] text-ink-muted">
              <span className="font-mono">{member.uhid}</span> · {member.mobile}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">Balance</div>
            <div className="text-[22px] font-bold leading-none">{balance}</div>
            <div className="text-[11px] text-ink-muted">{rupees(balance * pointValue)}</div>
          </div>
        </div>

        <div className="mt-3 grid gap-2 border-t border-line pt-3 text-[11.5px] text-ink-muted sm:grid-cols-3">
          <div>Lifetime earned: <b className="text-ink">{member.lifetime_earned}</b></div>
          <div>Lifetime used: <b className="text-ink">{member.lifetime_redeemed}</b></div>
          <div>Lifetime expired: <b className="text-ink">{member.lifetime_expired}</b></div>
        </div>
      </Card>

      {error && <div className="rounded-lg bg-danger-soft px-3 py-2 text-xs text-danger-ink">{error}</div>}

      <Card>
        <CardHeader>
          <CardTitle><Coins size={15} /> Manual adjustment</CardTitle>
          {!adjusting && <Button size="sm" variant="ghost" onClick={() => setAdjusting(true)}>Adjust points</Button>}
        </CardHeader>

        {adjusting ? (
          <div className="space-y-3">
            <div className="grid gap-2 sm:grid-cols-[auto_1fr]">
              <div className="inline-flex h-9 rounded-lg border border-border p-0.5">
                {([['add', 'Add', Plus], ['deduct', 'Deduct', Minus]] as const).map(([id, label, Icon]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setDirection(id)}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-md px-3 text-[12px] font-medium transition-colors',
                      direction === id ? 'bg-teal text-white' : 'text-ink-muted hover:bg-muted'
                    )}
                  >
                    <Icon size={12} /> {label}
                  </button>
                ))}
              </div>
              <Input
                type="number"
                min={1}
                value={amount}
                placeholder="Points"
                className="tabular-nums"
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>

            <FormField label="Reason" required>
              <Textarea
                rows={2}
                value={note}
                placeholder="e.g. goodwill after a dispensing error; correcting a mis-keyed redemption"
                onChange={(e) => setNote(e.target.value)}
              />
            </FormField>

            <div className="flex gap-2">
              <Button size="sm" disabled={saving} loading={saving} onClick={submit}>
                {direction === 'add' ? 'Add points' : 'Deduct points'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setAdjusting(false); setError(''); }}>Cancel</Button>
            </div>
          </div>
        ) : (
          <p className="text-[11.5px] leading-relaxed text-ink-muted">
            A goodwill grant, or the correction of an operator error. It becomes an ordinary ledger
            row like any other — attributed to you, carrying your reason — so there is no way to
            change a balance that does not leave a trace.
          </p>
        )}
      </Card>

      <Card>
        <CardHeader>
          <CardTitle><Sparkles size={15} /> Statement</CardTitle>
          {rows && <Badge variant="neutral">{rows.length} entries</Badge>}
        </CardHeader>

        <p className="-mt-1.5 mb-3 text-[11.5px] text-ink-muted">
          Every movement, newest first. The balance above is the sum of this column — which is what
          makes "where did my points go?" answerable rather than a matter of trust.
        </p>

        {!rows ? (
          <LoadingState />
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-[13px] text-ink-muted">
            Nothing recorded yet
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>What</TableHead>
                  <TableHead className="text-right">Points</TableHead>
                  <TableHead>Against</TableHead>
                  <TableHead>Note</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap text-[11.5px] text-ink-muted">
                      {shortDateTime(r.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="text-[12px] font-medium">{ENTRY_LABEL[r.entry_type] ?? r.entry_type}</div>
                      {r.expires_at && r.points > 0 && (
                        <div className="text-[10.5px] text-ink-muted">expires {shortDate(r.expires_at)}</div>
                      )}
                    </TableCell>
                    <TableCell
                      className={cn(
                        'text-right font-semibold tabular-nums',
                        r.points > 0 ? 'text-success-ink' : 'text-danger-ink'
                      )}
                    >
                      {r.points > 0 ? `+${r.points}` : r.points}
                    </TableCell>
                    <TableCell className="whitespace-nowrap font-mono text-[11px] text-ink-muted">
                      {r.bill_number || r.return_number || '—'}
                    </TableCell>
                    <TableCell className="text-[11.5px] text-ink-muted">
                      {r.note || '—'}
                      {r.created_by && <div className="text-[10.5px] text-ink-muted/75">by {r.created_by}</div>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
